import React, { useState, useRef, useContext } from 'react';
import { useTranslation } from "react-i18next";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Tooltip } from 'primereact/tooltip';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import CustomDropdown from '../../../../custom/customDropdown';
import { AuthContext } from '../../../../Header/auth - context';
import { useSelector } from 'react-redux';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const emptyForm = {
    Naziv: '',
    DaniOd: null,
    DaniDo: null,
    TipDnevnice: null,
    Iznos: null,
    Valuta: null,
    VazenjeOd: null,
    VazenjeDo: null,
    Napomena: '',
    ID_Drzava: null,
    Procenat: null,
};

/* TipDnevnice grupisanje za dinamičke fieldove */
const showDaniOdDo  = (tip) => tip === 'PERIOD';
const showIznosValuta = (tip) => tip && tip !== 'PROCENAT';
const showProcenat  = (tip) => tip === 'PROCENAT';

const DnevniceObracunTab = ({ options, dnevniceDB, onRefresh, toast }) => {
  const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);
    const formRef = useRef(null);
    const [formData, setFormData] = useState(emptyForm);
    const [selectedId, setSelectedId] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);

    const resetForm = () => { setFormData(emptyForm); setSelectedId(null); setFormSubmitted(false); };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleTipChange = (e) => {
        const tip = e.target.value;
        setFormData(prev => ({
            ...prev,
            TipDnevnice: tip,
            DaniOd: showDaniOdDo(tip) ? prev.DaniOd : null,
            DaniDo: showDaniOdDo(tip) ? prev.DaniDo : null,
            Iznos: showIznosValuta(tip) ? prev.Iznos : null,
            Valuta: showIznosValuta(tip) ? prev.Valuta : null,
            Procenat: showProcenat(tip) ? prev.Procenat : null,
        }));
    };

    const handleRowSelect = (row) => {
        const id = row?.id;
        setSelectedId(prev => {
            const next = String(prev) === String(id) ? null : id;
            if (next !== null) {
                setFormData({
                    Naziv: row.Naziv || '',
                    DaniOd: row.DaniOd != null ? Number(row.DaniOd) : null,
                    DaniDo: row.DaniDo != null ? Number(row.DaniDo) : null,
                    TipDnevnice: row.TipDnevnice || null,
                    Iznos: row.Iznos != null ? Number(row.Iznos) : null,
                    Valuta: row.Valuta || null,
                    VazenjeOd: parseDateStr(row.VazenjeOd),
                    VazenjeDo: parseDateStr(row.VazenjeDo),
                    Napomena: row.Napomena || '',
                    ID_Drzava: row.ID_Drzava || null,
                    Procenat: row.Procenat != null ? Number(row.Procenat) : null,
                });
            } else {
                setFormData(emptyForm);
                setFormSubmitted(false);
            }
            return next;
        });
    };

    const parseDateStr = (str) => {
        if (!str) return null;
        const parts = str.replace(/\./g, ' ').trim().split(/\s+/);
        if (parts.length < 3) return null;
        const d = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
        return isNaN(d.getTime()) ? null : d;
    };

    const toUtcIso = (d) => {
        if (!d) return null;
        const x = d instanceof Date ? d : new Date(d);
        const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
        return utc.toISOString().slice(0, 19) + 'Z';
    };

    const handleSave = (e) => {
        e.preventDefault();
        setFormSubmitted(true);
        if (!formData.TipDnevnice) return;
        if (showIznosValuta(formData.TipDnevnice) && (!formData.Iznos || !formData.Valuta)) return;
        if (showProcenat(formData.TipDnevnice) && !formData.Procenat) return;
        if (!formData.VazenjeOd || !formData.VazenjeDo || !formData.Naziv) return;
        confirmDialog({
            message: selectedId ? 'Sačuvati izmene dnevnice?' : 'Dodati novu dnevnicu?',
            acceptLabel: 'Da', rejectLabel: 'Ne',
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () => doSave(),
        });
    };

    const doSave = () => {
        const payload = { ...formData, VazenjeOd: toUtcIso(formData.VazenjeOd), VazenjeDo: toUtcIso(formData.VazenjeDo) };
        fetch(process.env.REACT_APP_BACKEND_URL + '/trip/ManageObracunSifarnik', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ step: 'SAVE_DNEVNICA', id: selectedId || 0, formData: payload }),
        })
            .then(r => r.json().then(d => ({ ok: r.ok, d })))
            .then(({ ok, d }) => {
                if (!ok) throw new Error(d.message || 'Server error');
                const msg = d[0]?.message || '';
                if (msg.includes('Nema izmena')) {
                    toast.current.show({ severity: 'warn', summary: msg, life: 3000 });
                    return;
                }
                toast.current.show({ severity: 'success', summary: msg || 'Uspešno!', life: 3000 });
                resetForm();
                onRefresh();
            })
            .catch(err => toast.current.show({ severity: 'error', summary: t('admin.m_greska_pri_cuvanju'), detail: err?.message || String(err), life: 8000 }));
    };

    const handleDelete = () => {
        if (!selectedId) { toast.current.show({ severity: 'warn', summary: t('admin.m_izaberite_dnevnicu_za_brisanje'), life: 3000 }); return; }
        confirmDialog({
            message: 'Obrisati dnevnicu?',
            acceptLabel: 'Da', rejectLabel: 'Ne',
            icon: 'pi pi-info-circle', acceptClassName: 'p-button-danger',
            accept: () => {
                fetch(process.env.REACT_APP_BACKEND_URL + '/trip/ManageObracunSifarnik', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
                    body: JSON.stringify({ step: 'DELETE_DNEVNICA', id: selectedId }),
                })
                    .then(r => r.json())
                    .then(() => { toast.current.show({ severity: 'success', summary: t('admin.m_dnevnica_obrisana'), life: 3000 }); resetForm(); onRefresh(); })
                    .catch(() => toast.current.show({ severity: 'error', summary: t('admin.m_greska_pri_brisanju'), life: 3000 }));
            },
        });
    };

    const invalid = (val) => formSubmitted && (val === null || val === '' || val === undefined);

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Dnevnice obračun');
        const headers = ['Naziv', 'Tip dnevnice', 'Dani od', 'Dani do', 'Iznos', 'Valuta', 'Procenat (%)', 'Država', 'Važi od', 'Važi do', 'Napomena'];
        const headerRow = worksheet.addRow(headers);
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
            cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
        });
        (dnevniceDB || []).forEach(r => {
            const row = worksheet.addRow([
                r.Naziv || '',
                r.TipDnevnice || '',
                r.DaniOd != null ? Number(r.DaniOd) : '',
                r.DaniDo != null ? Number(r.DaniDo) : '',
                r.Iznos != null ? Number(r.Iznos).toFixed(2) : '',
                r.ValutaNaziv || '',
                r.Procenat != null ? Number(r.Procenat).toFixed(2) : '',
                r.DrzavaNaziv || '',
                r.VazenjeOd || '',
                r.VazenjeDo || '',
                r.Napomena || '',
            ]);
            row.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        worksheet.columns.forEach(col => {
            let max = 10;
            col.eachCell(cell => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade' }).replace(/,/g, '');
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `Dnevnice obracun - ${company?.companyName || ''} - ${dt}.xlsx`);
    };

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <Tooltip target=".export-buttons>button" position="bottom" />
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                {selectedId && <Button type="button" icon="pi pi-trash" severity="danger" rounded onClick={handleDelete} data-pr-tooltip={t('common.delete')} />}
            </div>
            <input type="search" placeholder={t('common.search')} className="p-inputtext p-component"
                onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    const tip = formData.TipDnevnice;

    return (
        <div style={{ marginTop: '1%' }}>
            <Tooltip target=".field-tooltip" position="top" />
            <ConfirmDialog />
            <div className="blagajnaDashboard dnevniceDashboard">
                <Card>
                    <form autoComplete="off" ref={formRef}>
                        <div className="gridLicni">
                            <div className="col-12 md:col-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('admin.naziv')}:</label>
                                <InputText name="Naziv" value={formData.Naziv} onChange={handleChange}
                                    required className={invalid(formData.Naziv) ? 'p-invalid' : ''} />
                            </div>
                            <div className="col-12 md:col-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('admin.tipDnevnice')}:</label>
                                <CustomDropdown name="TipDnevnice" value={formData.TipDnevnice}
                                    options={options.TipDnevniceDB} optionLabel="label" placeholder={t('magacin.phTip')}
                                    required invalid={invalid(formData.TipDnevnice)} onChange={handleTipChange} />
                            </div>

                            {showDaniOdDo(tip) && (
                                <>
                                    <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label>{t('admin.daniOd')}:</label>
                                        <InputNumber name="DaniOd" value={formData.DaniOd}
                                            onValueChange={handleChange} minFractionDigits={0} maxFractionDigits={0}
                                            required className={invalid(formData.DaniOd) ? 'p-invalid' : ''} />
                                    </div>
                                    <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label>Dani do <i className="pi pi-info-circle field-tooltip" data-pr-tooltip="Prazan = neograničeno (nema gornje granice broja dana)" style={{ marginLeft: '3px', color: '#888', cursor: 'pointer' }} /></label>
                                        <InputNumber name="DaniDo" value={formData.DaniDo}
                                            onValueChange={handleChange} minFractionDigits={0} maxFractionDigits={0} />
                                    </div>
                                </>
                            )}

                            {showIznosValuta(tip) && (
                                <>
                                    <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label>{t('nova.iznos')}:</label>
                                        <InputNumber name="Iznos" value={formData.Iznos}
                                            onValueChange={handleChange} minFractionDigits={2} maxFractionDigits={2}
                                            required className={invalid(formData.Iznos) ? 'p-invalid' : ''} />
                                    </div>
                                    <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                        <label>{t('nova.valuta')}:</label>
                                        <CustomDropdown name="Valuta" value={formData.Valuta}
                                            options={options.ValuteDB} optionLabel="label" placeholder={t('nova.valuta')}
                                            required invalid={invalid(formData.Valuta)} onChange={handleChange} />
                                    </div>
                                </>
                            )}

                            {showProcenat(tip) && (
                                <div className="col-12 md:col-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                    <label>{t('admin.procenatPct')}:</label>
                                    <InputNumber name="Procenat" value={formData.Procenat}
                                        onValueChange={handleChange} minFractionDigits={2} maxFractionDigits={2} suffix=" %"
                                        required className={invalid(formData.Procenat) ? 'p-invalid' : ''} />
                                </div>
                            )}

                            <div className="col-12 md:col-6" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>Država: <i className="pi pi-info-circle field-tooltip" data-pr-tooltip="Prazan = važi za sve države. Specifična država ima prednost u obračunu." style={{ marginLeft: '3px', color: '#888', cursor: 'pointer' }} /></label>
                                <CustomDropdown name="ID_Drzava" value={formData.ID_Drzava}
                                    options={options.DrzaveDB} optionLabel="label" placeholder={t('admin.sveDrzave')}
                                    onChange={handleChange} showClear />
                            </div>

                            <div className="col-12 md:col-6">
                                <label>{t('ture.vaziOd')}:</label>
                                <Calendar name="VazenjeOd" value={formData.VazenjeOd} onChange={handleChange}
                                    showIcon dateFormat="dd.mm.yy" mask="99.99.9999"
                                    required className={invalid(formData.VazenjeOd) ? 'p-invalid' : ''} />
                            </div>
                            <div className="col-12 md:col-6">
                                <label>{t('ture.vaziDo')}:</label>
                                <Calendar name="VazenjeDo" value={formData.VazenjeDo} onChange={handleChange}
                                    showIcon dateFormat="dd.mm.yy" mask="99.99.9999"
                                    required className={invalid(formData.VazenjeDo) ? 'p-invalid' : ''} />
                            </div>
                            <div className="col-12 md:col-12" style={{ display: 'flex', flexDirection: 'column' }}>
                                <label>{t('nova.napomena')}:</label>
                                <InputText name="Napomena" value={formData.Napomena} onChange={handleChange} />
                            </div>
                        </div>
                        <div className="formUnosZap buttonUnosZap">
                            <button type="submit" onClick={handleSave} style={{ minWidth: 'fit-content', marginTop: '2%' }} className="btn btn-success">
                                {selectedId ? 'SAČUVAJ' : 'UNOS'}
                            </button>
                        </div>
                    </form>
                </Card>
                <Card>
                    <DataTable className="stavke-tabela" value={dnevniceDB || []} paginator rows={10} scrollable scrollHeight="45vh"
                        header={tableHeader} emptyMessage="Nema dnevnica" resizableColumns
                        globalFilter={globalFilter}
                        rowClassName={row => String(row.id) === String(selectedId) ? 'selected-row' : ''}>
                        <Column header={t('common.choose')} style={{ minWidth: '90px' }}
                            body={rowData => (
                                <Button label={t('common.choose')} icon="pi pi-eye" type="button"
                                    className="p-button-text p-button-sm" onClick={() => handleRowSelect(rowData)} />
                            )} />
                        <Column field="Naziv" header={t('admin.naziv')} sortable style={{ minWidth: '150px' }} />
                        <Column field="TipDnevnice" header={t('nova.tip')} sortable style={{ minWidth: '120px' }}
                            body={r => {
                                const opt = (options.TipDnevniceDB || []).find(o => o.value === r.TipDnevnice);
                                return opt?.label || r.TipDnevnice || '-';
                            }} />
                        <Column field="DaniOd" header={t('admin.daniOd')} style={{ minWidth: '90px' }} body={r => r.DaniOd ?? '-'} />
                        <Column field="DaniDo" header={t('admin.daniDo')} style={{ minWidth: '90px' }} body={r => r.DaniDo ?? '-'} />
                        <Column field="Iznos" header={t('nova.iznos')} sortable style={{ minWidth: '110px' }}
                            body={r => r.Iznos != null ? `${Number(r.Iznos).toFixed(2)} ${r.ValutaNaziv || ''}` : '-'} />
                        <Column field="Procenat" header={t('admin.procenat')} style={{ minWidth: '100px' }}
                            body={r => r.Procenat != null ? `${Number(r.Procenat).toFixed(2)} %` : '-'} />
                        <Column field="DrzavaNaziv" header={t('ture.drzava')} style={{ minWidth: '120px' }} body={r => r.DrzavaNaziv || '-'} />
                        <Column field="VazenjeOd" header={t('ture.vaziOd')} sortable style={{ minWidth: '110px' }} />
                        <Column field="VazenjeDo" header={t('ture.vaziDo')} sortable style={{ minWidth: '110px' }} />
                        <Column field="Napomena" header={t('nova.napomena')} style={{ minWidth: '150px' }} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

export default DnevniceObracunTab;
